import React from 'react';
import _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { ILicensesScope } from '../../../ts/controllers/LicensesCtrl';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { ILicensingInfoWithTotals, LicensesService } from '../../../ts/services/LicensesService';
import { ServiceLocator } from '../../ServiceLocator';
import { RouterError } from '../RouterHelpers/RouterError';

const template = `<div ng-controller="LicensesCtrl" ng-include="'serverSettingsLicensesTab.html'"></div>`;

export interface LicensesSettingsSubspaceOwnProps {
  readonly angularContext: AngularContext;
}

export interface LicensesSettingsSubspaceAngularProps {
  readonly licenses: LicensesService;
}

export type LicensesSettingsSubspaceProps = LicensesSettingsSubspaceOwnProps & LicensesSettingsSubspaceAngularProps;

export interface LicensesSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly licensingInfo: ILicensingInfoWithTotals;
}

export class LicensesSettingsSubspaceComponent extends React.PureComponent<LicensesSettingsSubspaceProps, LicensesSettingsSubspaceState> {

  private childScope: ILicensesScope = undefined;
  private unmounted: boolean = false;

  public constructor(props: LicensesSettingsSubspaceProps) {
    super(props);
    this.state = {hasDataToRender: false, hasError: false, licensingInfo: undefined};
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate() {
    if (!_.isUndefined(this.state.licensingInfo)) {
        this.updateState({hasDataToRender: true});
    }

    if (this.state.hasDataToRender && !_.isUndefined(this.childScope)) {
      _.defer(() => this.childScope.$apply());
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      return <RouterError errorType={'server-default'} urls={undefined}/>;
    }

    // the parent div here is important to ensure AngularDirective plays nice when unmounting
    return (
      <div className={'tb-stylish-settings'}>
        <AngularDirective
          template={template}
          angularContext={this.props.angularContext}
          setupScope={this.setupScope}
        />
      </div>
    );
  }

  private updateState<K extends keyof LicensesSettingsSubspaceState>(newState: Pick<LicensesSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData() {
    this.getlicensingInfo();
  }

  private getlicensingInfo = async () => {
    try {
      const licensingInfo = await this.props.licenses.fetch();
      this.updateState({licensingInfo});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as ILicensesScope;
    childScope.licensingInfo = this.state.licensingInfo;
    this.childScope = childScope;
    return childScope;
  }
}

const LicensesSettingsHOC = angularProviderHOC<LicensesSettingsSubspaceOwnProps, LicensesSettingsSubspaceAngularProps, LicensesSettingsSubspaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    licenses: ServiceLocator.get<LicensesService>('LicensesService'),
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

export const LicensesSettingsSubspace: React.ComponentType<LicensesSettingsSubspaceOwnProps>
  = LicensesSettingsHOC(LicensesSettingsSubspaceComponent);
